// src/modules/invoices/pages/InvoicesListPage.tsx
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  InputAdornment,
  IconButton,
  useTheme,
  useMediaQuery,
  Skeleton,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  ReceiptLong as ReceiptIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import api from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";
import { useProtectedRoute } from "../../../hooks/useProtectedRoute";

type Invoice = {
  _id: string;
  invoiceNumber?: string;
  clientId?: { fullName?: string; phone?: string; email?: string };
  createdBy?: { fullName?: string };
  createdAt?: string;
  grandTotal?: number;
  paymentStatus?: "PAID" | "PARTIALLY PAID" | "UNPAID" | "DRAFT" | string;
};

export default function InvoicesListPage() {
  useProtectedRoute();
  const { token } = useAuth();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [loading, setLoading] = useState(false);

  const heroGradient = useMemo(
    () =>
      `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 55%, #0f172a 100%)`,
    [theme]
  );
  const surfaceBorder = `1px solid ${theme.palette.mode === "light" ? "#e5e7eb" : theme.palette.divider}`;

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const res = await api.get(`/invoices?search=${encodeURIComponent(debounced)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!mounted) return;
        setInvoices(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (!mounted) return;
        setInvoices([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [debounced, token]);

  const fmtMoney = (n?: number) =>
    typeof n === "number"
      ? Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(n)
      : "-";

  const fmtDate = (iso?: string) =>
    iso ? new Date(iso).toLocaleDateString() : "-";
  const fmtTime = (iso?: string) =>
    iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "-";

  const statusColor = (status?: string) => {
    switch (status) {
      case "PAID":
        return "success";
      case "PARTIALLY PAID":
      case "PARTIAL":
        return "warning";
      case "UNPAID":
        return "error";
      case "DRAFT":
        return "default";
      default:
        return "info";
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, pb: { xs: 1, md: 2 } }}>
      {/* HERO / HEADER */}
      <Card
        component={motion.div}
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 3,
          overflow: "hidden",
          border: surfaceBorder,
          background: heroGradient,
          color: "white",
        }}
      >
        <CardContent sx={{ py: { xs: 2.25, md: 2.75 } }}>
          <Grid container alignItems="center" spacing={2} justifyContent="space-between">
            <Grid item xs={12} md="auto">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Avatar
                  sx={{
                    bgcolor: "rgba(255,255,255,0.15)",
                    border: "1px solid rgba(255,255,255,0.18)",
                    width: 44,
                    height: 44,
                    backdropFilter: "blur(4px)",
                  }}
                >
                  <ReceiptIcon />
                </Avatar>
                <Box>
                  <Typography variant={isSmDown ? "h5" : "h4"} sx={{ fontWeight: 900, lineHeight: 1.15 }}>
                    Invoices
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Track billing, payment status, and balances at a glance.
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md>
              <Box
                sx={{
                  display: "flex",
                  gap: 1.25,
                  flexWrap: "wrap",
                  justifyContent: { xs: "stretch", md: "flex-end" },
                  mt: { xs: 1, md: 0 },
                }}
              >
                <TextField
                  placeholder="Search by invoice #, client, phone, or email…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  size="small"
                  fullWidth={isSmDown}
                  sx={{
                    minWidth: { xs: "100%", md: 360 },
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "rgba(255,255,255,0.92)",
                      "& fieldset": { borderColor: "transparent" },
                      "&:hover fieldset": { borderColor: "transparent" },
                      "&.Mui-focused fieldset": { borderColor: "transparent" },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<AddIcon />}
                  onClick={() => navigate("/invoices/create")}
                  sx={{
                    fontWeight: 800,
                    px: 2.2,
                    boxShadow: "none",
                    "&:hover": { boxShadow: 2 },
                  }}
                >
                  Create Invoice
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* CONTENT CARD */}
      <Card
        component={motion.div}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.05 }}
        elevation={0}
        sx={{
          borderRadius: 3,
          border: surfaceBorder,
          overflow: "hidden",
        }}
      >
        <TableContainer component={Paper} elevation={0} sx={{ maxHeight: { md: 560 }, overflow: "auto" }}>
          <Table stickyHeader size="medium" aria-label="invoices-table">
            <TableHead>
              <TableRow
                sx={{
                  "& th": { backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "action.hover", fontWeight: 800 },
                }}
              >
                <TableCell>Invoice #</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Time</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    <TableCell colSpan={10} sx={{ py: 1.25 }}>
                      <Skeleton height={28} />
                    </TableCell>
                  </TableRow>
                ))
              ) : invoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10}>
                    <Box
                      sx={{
                        py: 6,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        gap: 1,
                        color: "text.secondary",
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: theme.palette.mode === "light" ? "#eef2ff" : "primary.dark",
                          color: "primary.main",
                          width: 56,
                          height: 56,
                        }}
                      >
                        <ReceiptIcon />
                      </Avatar>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, mt: 0.5 }}>
                        No invoices found
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Try a different search or create a new invoice.
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => navigate("/invoices/create")}
                        sx={{ fontWeight: 800 }}
                      >
                        Create Invoice
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                invoices.map((inv) => {
                  const date = fmtDate(inv.createdAt);
                  const time = fmtTime(inv.createdAt);
                  return (
                    <TableRow
                      key={inv._id}
                      hover
                      sx={{
                        "&:hover": {
                          background:
                            theme.palette.mode === "light"
                              ? "linear-gradient(0deg, rgba(37,99,235,0.05), rgba(37,99,235,0.05))"
                              : "rgba(37,99,235,0.08)",
                        },
                      }}
                    >
                      <TableCell sx={{ fontWeight: 700 }}>{inv.invoiceNumber || "—"}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {inv.clientId?.fullName || "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>{inv.clientId?.phone || <Typography color="text.disabled">—</Typography>}</TableCell>
                      <TableCell>{inv.clientId?.email || <Typography color="text.disabled">—</Typography>}</TableCell>
                      <TableCell>{date}</TableCell>
                      <TableCell>{time}</TableCell>
                      <TableCell>{inv.createdBy?.fullName || "—"}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{fmtMoney(inv.grandTotal)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={inv.paymentStatus || "—"}
                          color={statusColor(inv.paymentStatus)}
                          sx={{ fontWeight: 700, textTransform: "uppercase" }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        <IconButton onClick={() => navigate(`/invoices/${inv._id}`)} color="primary">
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton onClick={() => navigate(`/invoices/${inv._id}/edit`)} color="primary">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Space before fixed footer seam */}
      <Box sx={{ height: 12 }} />
    </Box>
  );
}
